import { resolveGalleryImageUrl } from "@/utils/helpers";
import { imageGalleryQueryOptions } from "@labeling-tools/api/lebron/react-query-hooks";
import { useQuery } from "@tanstack/react-query";

export interface GalleryImgUrl {
  /**
   * Direct URL to the server-optimized (compressed/downscaled) image rendition,
   * falling back to the original when no optimized variant exists. Named
   * `objUrl` to stay a drop-in for the old signed-blob hook — but it's a plain
   * URL now, not a blob object URL, so there's nothing to revoke.
   */
  objUrl: string;
  /** Capture time of the image (ISO string), when the API provides it. */
  timestamp?: string;
  /**
   * Whether the gallery actually returned an `optimized_image` (vs. falling
   * back to the full-res original). Recorded in analytics to confirm the
   * optimization is applying in prod.
   */
  optimized: boolean;
}

/**
 * Load a coverage-beta image through the shared `/v1/images/gallery` endpoint
 * and hand back its OPTIMIZED rendition. Replaces `useFetchSignedImgObjUrl`,
 * which downloaded the full-resolution GCS original as a blob — on slow
 * bandwidth that dominates task-load time. This mirrors the win the OOS
 * confirmation flow adopted in OOSv1.11.0 (optimized_image via the gallery).
 */
export const useFetchGalleryImgUrl = (image_id: string) => {
  return useQuery({
    ...imageGalleryQueryOptions(image_id ? [image_id] : undefined),
    select: (data): GalleryImgUrl => {
      const img = data.images?.[0];
      return {
        objUrl: resolveGalleryImageUrl(img) ?? "",
        timestamp: img?.timestamp,
        optimized: !!img?.optimized_image,
      };
    },
  });
};
